import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Pmp,
  PmpAdminStatus,
  PmpStatus,
  PmpsWithPagination
} from 'core/pmp/Pmp';
import _ from 'lodash';
import { Pageable } from 'ws/Pageable';
import { useCoreContext } from 'contexts/coreContext';
import { useCallAPI } from 'hooks/useCallAPI';
import { DefaultPmpManager } from 'core/pmp/PmpManager';
import {
  PmpDurationError,
  usePmpDurationValidator
} from './PmpDurationValidator';
import i18n from 'i18n';
import {
  getCustomRenderColumn,
  getDateRangeColumn,
  getLabelValueColumn,
  getNameIdColumn,
  getStatusDesColumn,
  getStringArrayColumn,
  headerWithIconFormatter,
  renderColumn,
  sortableColumn
} from 'components/TableColumn/TableColumn';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { renderPmpDeviceColumn } from './PmpDevicesColumn';
import { getCurrencyLabel } from 'helper/CurrencyHelper';

export enum PmpListColumns {
  NAME = 'id',
  STATUS = 'adminStatus',
  AGENCYID = 'adAgencyIds',
  SCHEDULE = 'startTime',
  BUDGET = 'budget',
  SPACES = 'spaces',
  CHANNEL_ID = 'channelGroupId'
}

const pmpManager = new DefaultPmpManager();

export const usePmpListModel = (
  spaceOptions: SelectOptions[],
  singleSelect?: boolean,
  defaultSelectedPmps: number[] = [],
  pmpList?: Pmp[],
  editable = true,
  showStatus = true,
  showAgency = true,
  showChannel = true
) => {
  const fetchPmpsFromServer = !pmpList;
  const core = useCoreContext();
  const [pmps, setPmps] = useState<Pmp[]>(_.defaultTo(pmpList, []));
  const [searchString, setSearchString] = useState<string>('');
  const [selectedPmps, setSelectedPmps] =
    useState<number[]>(defaultSelectedPmps);
  const [pageable, setPageable] = useState<Pageable & { totalCount: number }>({
    page: 1,
    sizePerPage: 10,
    sort: 'id',
    direction: 'desc',
    totalCount: pmpList ? pmpList.length : 0
  });
  const [pmpDurationError, setPmpDurationError] = useState<
    (PmpDurationError & Pmp) | undefined
  >();
  const currency = _.get(core, 'accountManager.localeMeta.currency', 'NTD');
  const { loading, callAPIs } = useCallAPI();
  const {
    loading: loadingDurationValidator,
    fetchPmpWithinDuration,
    // validatePmpDuration,
    clearCacheWithin
  } = usePmpDurationValidator();

  const fetchInvalidPmpWithinDuration = useCallback(
    (pmps: Pmp[]) => {
      const inactivePmps: Pmp[] = pmps.filter(
        (pmp) => pmp.status === PmpStatus.INACTIVE
      );
      inactivePmps.forEach((pmp) =>
        fetchPmpWithinDuration(pmp.startTime, pmp.endTime)
      );
    },
    [fetchPmpWithinDuration]
  );

  const fetchPmps = useCallback(
    (page = 1) => {
      callAPIs(
        [
          () =>
            pmpManager.getPmpList(
              {
                page,
                sort: pageable.sort,
                direction: pageable.direction,
                sizePerPage: pageable.sizePerPage
              },
              searchString
            )
        ],
        (pmpsWithPagination: PmpsWithPagination) => {
          setPmps(pmpsWithPagination.pmps);
          setPageable((prev) => ({
            ...prev,
            page: pmpsWithPagination.pagination.page,
            sizePerPage: pmpsWithPagination.pagination.size,
            totalCount: pmpsWithPagination.pagination.totalCount
          }));
          fetchInvalidPmpWithinDuration(pmpsWithPagination.pmps);
        }
      );
    },
    [
      pageable.sort,
      pageable.direction,
      pageable.sizePerPage,
      searchString,
      fetchInvalidPmpWithinDuration,
      callAPIs
    ]
  );

  const getPmpStatusFilterString = useCallback((status: PmpAdminStatus) => {
    return i18n.t(`pmp.adminStatus.${_.camelCase(PmpAdminStatus[status])}`);
  }, []);

  const getPmpStatusColor = useCallback((status: PmpAdminStatus) => {
    switch (status) {
      case PmpAdminStatus.BINDING:
        return 'primary';
      case PmpAdminStatus.STOP:
        return 'black';
      case PmpAdminStatus.EXPIRED:
        return 'danger';
      case PmpAdminStatus.FINISH:
      case PmpAdminStatus.NOT_BINDING:
        return 'light';
      default:
        return '';
    }
  }, []);

  const getPmpStatusDesData = useCallback(
    (status: PmpAdminStatus) => {
      return {
        des: getPmpStatusFilterString(status),
        color: getPmpStatusColor(status)
      };
    },
    [getPmpStatusFilterString, getPmpStatusColor]
  );

  const handleTableChange = useCallback(
    (type, props) => {
      if (type === 'sort') {
        (props.sortField !== pageable.sort ||
          props.sortOrder !== pageable.direction) &&
          setPageable((prev) => ({
            ...prev,
            page: 1,
            sort: props.sortField,
            direction: props.sortOrder
          }));
        return;
      }
      fetchPmps(props.page);
    },
    [pageable.sort, pageable.direction, fetchPmps]
  );

  const onHandleSelect = useCallback(
    (id: string | number, select: boolean) => {
      if (singleSelect) {
        setSelectedPmps(select ? [+id] : []);
        return;
      }
      setSelectedPmps((prev) =>
        select
          ? _.uniq([...prev, +id])
          : prev.filter((p) => p.toString() !== id.toString())
      );
    },
    [singleSelect]
  );

  const onHandleSelectPage = useCallback(
    (select: boolean) => {
      const targetId = pmps.map((pmp) => pmp.id);
      setSelectedPmps((prev) =>
        select
          ? _.uniq([...prev, ...targetId])
          : prev.filter((p) => !targetId.includes(p))
      );
    },
    [pmps]
  );

  const clearSelect = useCallback(() => {
    setSelectedPmps([]);
  }, []);

  const onHandleSearch = useCallback((searchString: string) => {
    setSearchString(searchString);
  }, []);

  // const activatePmp = useCallback(() => {
  //   const pmp = pmps.find(pmp => selectedPmps.includes(pmp.id));
  //   if (!pmp) {
  //     return;
  //   }
  //   const pmpDurationError = validatePmpDuration(pmp);
  //   if (pmpDurationError) {
  //     setPmpDurationError({
  //       ...pmpDurationError,
  //       ...pmp
  //     });
  //   } else {
  //     callAPIs([() => pmpManager.updatePmpState(selectedPmps, 'activate')], () => {
  //       const pmp = pmps.find(pmp => selectedPmps.includes(pmp.id));
  //       // clear duration cache if pmp status is changed
  //       pmp && clearCacheWithin(pmp.startTime, pmp.endTime);
  //       fetchPmps();
  //       setSelectedPmps([]);
  //     });
  //   }
  // }, [pmps, selectedPmps, clearCacheWithin, validatePmpDuration, callAPIs, fetchPmps]);

  const deactivePmp = useCallback(() => {
    callAPIs(
      [() => pmpManager.updatePmpState(selectedPmps, 'deactivate')],
      () => {
        const pmp = pmps.find((pmp) => selectedPmps.includes(pmp.id));
        toast.success(i18n.t('pmpList.labels.deactivateSuccess'));
        // clear duration cache if pmp status is changed
        pmp && clearCacheWithin(pmp.startTime, pmp.endTime);
        fetchPmps();
        setSelectedPmps([]);
      }
    );
  }, [pmps, selectedPmps, clearCacheWithin, callAPIs, fetchPmps]);

  const cancelActivate = useCallback(() => {
    setPmpDurationError(undefined);
  }, []);

  const statusColumn = useMemo(
    () =>
      getStatusDesColumn(
        sortableColumn(
          PmpListColumns.STATUS,
          i18n.t('pmpList.headers.status'),
          true
        ),
        (pmp: Pmp) => getPmpStatusDesData(pmp.adminStatus)
      ),
    [getPmpStatusDesData]
  );

  const isPmpEditable = useCallback(
    (pmp: Pmp) => {
      return editable && pmpManager.isPmpEditable(pmp);
    },
    [editable]
  );

  const columns = useMemo(
    () =>
      _.compact([
        getNameIdColumn(
          sortableColumn(
            PmpListColumns.NAME,
            i18n.t('pmpList.headers.name'),
            true
          ),
          (pmp: Pmp) => (isPmpEditable(pmp) ? `/pmp/${pmp.id}/edit` : undefined)
        ),
        showStatus
          ? {
              ...statusColumn,
              formatExtraData: {
                ...statusColumn.formatExtraData,
                hint: i18n.t('pmpList.hints.pmpStatus'),
                icon: faInfoCircle
              },
              headerFormatter: headerWithIconFormatter
            }
          : undefined,
        getDateRangeColumn(
          sortableColumn(
            PmpListColumns.SCHEDULE,
            i18n.t('pmpList.headers.schedule'),
            true
          ),
          (pmp: Pmp) => ({ startDate: pmp.startTime, endDate: pmp.endTime })
        ),
        getLabelValueColumn(
          sortableColumn(
            PmpListColumns.BUDGET,
            i18n.t('pmpList.headers.budget'),
            true
          ),
          (pmp: Pmp) =>
            _.compact([
              {
                label: i18n.t('pmp.labels.pmpType'),
                value: i18n.t(`pmp.pmpTypes.${_.camelCase(pmp.pmpType)}`)
              },
              {
                label: i18n.t('pmp.labels.budget'),
                value: `${getCurrencyLabel(currency)} ${pmp.budget}`
              },
              undefined
            ])
        ),
        showChannel
          ? renderColumn(
              sortableColumn(
                PmpListColumns.CHANNEL_ID,
                i18n.t('pmpList.headers.channelGroupId'),
                true
              ),
              (value: string) => i18n.t(`salesChannels.${value}`)
            )
          : undefined,
        getCustomRenderColumn(
          sortableColumn(
            PmpListColumns.SPACES,
            i18n.t('pmpList.headers.space'),
            true
          ),
          (pmp: Pmp) => renderPmpDeviceColumn(pmp, spaceOptions)
        ),
        showAgency
          ? getStringArrayColumn(
              sortableColumn(
                PmpListColumns.AGENCYID,
                i18n.t('pmpList.headers.adAgencyId'),
                true
              ),
              (value: string | number) => i18n.t(`agency.name.${value}`)
            )
          : undefined
      ]),
    [
      showStatus,
      showAgency,
      showChannel,
      currency,
      spaceOptions,
      statusColumn,
      isPmpEditable
    ]
  );

  const filterdPmps = useMemo(() => {
    return pmps.filter((pmp) => {
      const nameMatch = pmp.name.includes(searchString);
      return nameMatch;
    });
  }, [pmps, searchString]);

  const batchOperations = useMemo(
    () => [
      {
        operation: {
          text: i18n.t('pmpList.buttons.deactivate'),
          enable: selectedPmps.length > 0,
          hint: '',
          onClick: deactivePmp
        }
      }
    ],
    [deactivePmp, selectedPmps]
  );

  useEffect(() => {
    fetchPmpsFromServer && fetchPmps();
  }, [fetchPmpsFromServer, fetchPmps]);

  return {
    loading: loading || loadingDurationValidator,
    columns,
    pageable,
    filterdPmps,
    selectedPmps,
    searchString,
    batchOperations,
    pmpDurationError,
    fetchPmps,
    clearSelect,
    isPmpEditable,
    cancelActivate,
    onHandleSearch,
    onHandleSelect,
    onHandleSelectPage,
    handleTableChange
  };
};
